import { Dialog } from '@components/dialog';
import { Box } from '@mui/material';
import { Grid } from '@mui/material';
import { useRef, useState } from 'react';
import { SearchChatHistoryView } from './SearchChatHistoryView';
import { useGetPolicy } from '@services/policy';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { useSearchMessages } from '@services/note/messages/useSearchMessages';
import { ChatViewWrapper } from './ChatViewWrapper';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { SearchField } from '@components/search';

export type MessagesDialogProps = {
  policyId: string;
  onClose: () => void;
  isOpen: boolean;
};

export const MessagesDialog = ({ policyId, onClose, isOpen }: MessagesDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const searchScrollRef = useRef<HTMLDivElement>(null);
  const [anchorId, setAnchorId] = useState<string | null>(null);

  const { data: policy } = useGetPolicy({ policyId });

  useStore((s) => s.userAuth, shallow);

  const formattedPolicyNumber = policy
    ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
    : '';

  const {
    messages: filteredSearchResults,
    isLoadingOlder: isLoadingOlderSearched,
    loadNewer: loadMoreSearched,
    pagination,
  } = useSearchMessages({
    policyId,
    params: {
      pageLimit: 15,
      beforeLimit: undefined,
      anchorId: undefined,
      searchText: searchTerm.length > 2 ? searchTerm : undefined,
    },
    enabled: !!searchTerm && searchTerm !== '' && searchTerm.length > 2,
  });

  const isSearchMode = searchTerm.trim().length > 2;

  const handleJumpToMessage = (id: string) => {
    setSearchTerm('');
    setAnchorId(id);
  };

  return (
    <Dialog
      open={isOpen}
      id='policy-messages-dialog'
      sx={{ flex: 'none', alignContent: 'baseline' }}
      title={`Policy #${formattedPolicyNumber} Chat`}
      subTitle='This contains chat history for all related transactions'
      maxWidth='lg'
      allowFullscreen={true}
      onClose={onClose}
      handleCloseDialog={onClose}
      primaryBtnText=''
      secondaryBtnText=''
      secondaryBtnAction={onClose}
      size='fullWidth'
      showBorders={false}
    >
      <Grid
        size={{ xs: 12 }}
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'border.default',
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: '80%' }, maxWidth: '100%' }}>
          <SearchField
            overrideValue={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            placeholder='Search Messages'
          />
        </Box>
      </Grid>
      <Grid
        container
        sx={{
          height: '70vh',
          flexDirection: 'column',
          overflowX: 'hidden',
          backgroundColor: isSearchMode ? 'background.main' : 'transparent',
        }}
      >
        {isSearchMode && (
          <SearchChatHistoryView
            results={filteredSearchResults}
            scrollRef={searchScrollRef}
            loadMore={loadMoreSearched}
            hasMore={filteredSearchResults?.length < (pagination?.total || 0)}
            isLoading={isLoadingOlderSearched}
            searchTerm={searchTerm}
            onSelectMessage={handleJumpToMessage}
          />
        )}

        <ChatViewWrapper policyId={policyId} isOpen={isOpen} isSearchMode={isSearchMode} anchorId={anchorId} />
      </Grid>
    </Dialog>
  );
};
